

import { Component, Vue, Watch } from "vue-property-decorator"
import { Getter, namespace, State } from "vuex-class"

import cloneDeep from "lodash/cloneDeep"

import { Area, AreaDetails, DataDefinition, DataSource, TreeNode } from "@/types"

import api from "@/services/api"

import { BModal } from "bootstrap-vue"
import vSelect from "vue-select"

import VuePerfectScrollbar from "vue-perfect-scrollbar"

import CadenceSchedules from "@/components/Config/Schedules/CadenceSchedules.vue"
import CssSetup from "@/components/Config/CssSetup.vue"
import DataModal from "@/components/Config/DataModal.vue"
import DefinitionSetup from "@/components/Config/Definitions/DefinitionSetup.vue"
import FieldSetup from "@/components/Config/Fields/FieldSetup.vue"
import PageWrapper from "@/components/New/Page/PageWrapper.vue"
import RuleSetup from "@/components/Config/Rules/RuleSetup.vue"
import ScrollTabs from "@/components/Controls/Tabs/ScrollTabs.vue"
import ScrollTab from "@/components/Controls/Tabs/ScrollTab.vue"
import SourceSetup from "@/components/Config/Sources/SourceSetup.vue"
import SourceSearch from "@/components/Config/Sources/SourceSearch.vue"
import TagSetup from "@/components/Config/Tags/TagSetup.vue"
import TemplateSetup from "@/components/Config/Template/TemplateSetup.vue"
import IntegrationSetup from "@/components/Config/Integrations/IntegrationSetup.vue"
import ViewSetup from "@/components/Config/Views/ViewSetup.vue"
import DomainSetup from "@/components/Config/Domain/DomainSetup.vue"
import TenantSettingsPage from "@/components/Config/TenantSettings/TenantSettingsPage.vue"
import { CurrentUser } from "@/types/user"
import FormatSetup from "@/components/Config/Formats/FormatSetup.vue"
import { TenantFeatures } from "@/types/tenantFeatures"

const setup = namespace("setup")
const caseStore = namespace("cases")

@Component({
	components: {
		"page": PageWrapper,
		"v-select": vSelect,
		"scrollbar": VuePerfectScrollbar,
		"field-setup": FieldSetup,
		"template-setup": TemplateSetup,
		"view-setup": ViewSetup,
		"definition-setup": DefinitionSetup,
		"source-setup": SourceSetup,
		"source-search": SourceSearch,
		"rule-setup": RuleSetup,
		"data-modal": DataModal,
		"tag-setup": TagSetup,
		"cadence-schedules": CadenceSchedules,
		"scroll-tab": ScrollTab,
		"scroll-tabs": ScrollTabs,
		"css-setup": CssSetup,
		"integration-setup": IntegrationSetup,
		"domain-setup": DomainSetup,
		"tenant-settings": TenantSettingsPage,
		"format-setup": FormatSetup
	}
})
export default class Configuration extends Vue {
	$refs!: {
		dataModal: BModal
	}

	@State
	me!: CurrentUser | null

	@Getter("getIsSuiteEnabled") isSuiteEnabled! : boolean;

	@Getter isFeatureEnabled!: (featureKey: TenantFeatures) => boolean;

	@setup.Action
	public loadTeams!: () => Promise<void>

	@setup.Action
	public loadDataSources!: () => Promise<void>

	@setup.Action
	public loadDefinitions!: () => Promise<void>

	@setup.Getter("getTeams")
	public teams!: any[]

	@setup.Getter("getDefinitions")
	public definitions!: DataDefinition[]

	@setup.State("dataSources")
	public allDataSources!: DataSource[];

	private usersDataSources: DataSource[] = [];

	@caseStore.Action
	public detailsLoad!: (refreshData: boolean) => void

	@caseStore.State("areas")
	public areas!: TreeNode<Area>[]

	public loading: boolean = true
	public filter: string = ""

	public async mounted () {
		await Promise.all([
			this.loadTeams(),
			this.loadDataSources(),
			this.loadDefinitions()
		])

		this.loading = false
	}

	private get dataSources (): DataSource[] {
		return this.usersDataSources
	}

	@Watch("allDataSources")
	private onAllDataSourcesChanged (newValue: DataSource[], oldValue: DataSource[]): void {
		// If this is the first update then get the users data sources
		if (!oldValue && !!newValue && newValue.length > 0) {
			this.updateDataSourcesForUser()
		} else {
			if (!!oldValue && !!newValue) {
				if (oldValue.length !== newValue.length) {
				// If there's a difference between the oldValue and newValue lengths then update
					this.updateDataSourcesForUser()
				} else if (!this.usersDataSources || this.usersDataSources.length === 0) {
					// If we haven't set the users Data Sources yet do that
					this.updateDataSourcesForUser()
				} else {
					// Otherwise check if there have been a change in Ids but not in lengths
					const newValueIds: string[] = newValue.map(nv => nv.id)
					const oldValueIds: string[] = oldValue.map(ov => ov.id)
					const newIds: string[] = newValueIds.filter(id => !oldValueIds.includes(id))

					// If there are new Ids then allDataSources has changed and we need to get the users data sources
					if (newIds.length > 0) {
						this.updateDataSourcesForUser()
					}
				}
			}
		}
	}

	public updateDataSourcesForUser (): void {
		if (this.me) {
			// If user has editData return all results
			if (this.me.editData) {
				this.usersDataSources = this.allDataSources
			} else {
				this.usersDataSources = []

				// If not restrict their results to just those they have permissions for
				if (this.me.teams) {
					const teams = this.me.teams
					const teamIds: string[] = teams.map(a => a.id)

					if (teamIds.length > 0) {
						// Get the data sources the user has access to
						this.usersDataSources = this.allDataSources.filter(ds => !!ds.editPermissions && ds.editPermissions.some((ep: string) => teamIds.includes(ep)))
					}
				}
			}
		}
	}

	public get columns (): any[] {
		return [
			{
				key: "title",
				label: "Title",
				sortable: true,
				errors: (item: any) => item.title ? null : "Please enter a title"
			},
			{
				key: "definitionTitle",
				label: "Item Type",
				sortable: true,
				dataType: "list",
				valueColumn: "definitionId",
				options: this.definitions,
				description: "Type of data in this list. Leave empty for a simple list."
			},
			{
				key: "includedLists",
				label: "Included Lists",
				dataType: "list-multiple",
				valueColumn: "includedLists",
				options: this.dataSources,
				description: "Include items from list in this one. It won't include any additional lists included to these",
				errors: (item: any) => this.checkForSelfDataSource(item)
			},
			{
				key: "editPermissions",
				label: "Edit Permissions",
				dataType: "list-multiple",
				valueColumn: "editPermissions",
				options: this.teams,
				description: "Which teams are able to edit items in this data source"
			},
			{
				key: "createdBy.name",
				label: "Created By",
				inModal: () => false,
				hasTemplate: true
			},
			{
				key: "lastUpdated",
				label: "Last Updated",
				inModal: () => false,
				hasTemplate: true
			},
			{
				key: "buttons",
				label: "",
				inModal: () => false,
				hasTemplate: true
			}
		]
	};

	public get filteredDataSources (): DataSource[] {
		if (!this.usersDataSources) {
			return []
		}

		if (this.filter === "") {
			return this.usersDataSources
		}

		const lowerFilter = this.filter.toLowerCase()

		return this.usersDataSources.filter(dataSource => dataSource.title.toLowerCase().indexOf(lowerFilter) > -1)
	}

	public get areaDefinitionId (): string | null {
		const definition = this.definitions.find(d => d.specialType === "area")

		return definition ? definition.id! : null
	}

	public get areaSource () {
		return {
			id: "areas",
			title: "Areas",
			extraFields: [
				{ key: "title", label: "Title", readonly: this.isSuiteEnabled, errors: (item: { title: string }) => (!item.title || item.title.trim() === "") ? "Please enter a title" : null },
				{ key: "address", label: "Address", dataType: "text-area", readonly: this.isSuiteEnabled },
				{ key: "location", label: "Location", dataType: "map-marker", readonly: this.isSuiteEnabled }],
			noEdit: true,
			hideUpload: true,
			definitionId: this.definitions ? this.areaDefinitionId : null,
			createItemModalTitle: "Create Area",
			getItems: () => api.areas(),
			itemDetails: (item: Area) => api.area(item.id),
			save: (newLocation: AreaDetails) => this.trySaveArea(newLocation),
			delete: (deleteLocation: AreaDetails) => this.tryDeleteArea(deleteLocation),
			treeLayout: true,
			treeItemSelectedModalTitle: "Area",
			treeCreateItemModalTitle: "Create sub-area"
		}
	}

	async trySaveArea (newArea: AreaDetails) {
		try {
			await api.areaSave(newArea)
		} catch (err) {
			this.$bvToast.toast(err, {
				title: `Error saving Area: ${newArea.title}`,
				variant: "danger",
				solid: true,
				autoHideDelay: 10000
			})
			if (!newArea.id) {
				return null
			}
			return api.area(newArea.id)
		}
	}

	async tryDeleteArea (area: AreaDetails) {
		try {
			await api.areaDelete(area)
		} catch (err) {
			this.$bvToast.toast(err, {
				title: `Error deleting Area: ${area.title}`,
				variant: "danger",
				solid: true,
				autoHideDelay: 10000
			})
			throw err
		}
	}

	async save (source: any) {
		source.includedLists = source.includedLists
			.map((listId: any) => listId.id
				? listId
				: this.dataSources.find((included: any) => included.id === listId))

		await api.dataSourceSave(source)

		await this.loadDataSources()
	}

	async dataSourceDeleteDetails (dataSource: any) {
		if (!dataSource!.id) {
			return
		}

		return api.dataSourceUsage(dataSource)
	}

	createNewSource () {
		this.$refs.dataModal.open({
			includedLists: []
		})
	}

	editSource (dataSource: any) {
		this.$refs.dataModal.open(cloneDeep(dataSource))
	}

	async deleteSource (dataSource: any) {
		await api.dataSourceDelete(dataSource)

		await this.loadDataSources()
	}

	private checkForSelfDataSource (item: any): string | null {
		if (item.includedLists.some((i: any) => i === item.id)) {
			return `Data source cannot be added to itself: ${item.title}`
		}

		return null
	}

	private get isPdfTemplateEnabled () {
		return this.isFeatureEnabled(TenantFeatures.PdfTemplates)
	}
}

